import { Application, Assets, Container, Rectangle, Sprite, Texture } from 'pixi.js';
import {
  CELL,
  TILE,
  buildPatternMeta,
  layerForItem,
  lookupAsset,
  versioned,
} from './tile-utils.js';
import { pcWallNudgeForFloor, isPcSkippedItem, pcGroundId } from './pc-wall-nudge.js';

const LAYER_ORDER = ['onbottom', 'bottom', 'top', 'borders', 'toppers'];
const SPRITE_SCALE = CELL / TILE;
/**
 * Lado do chunk em tiles. Os sprites vivem dentro de contêineres de chunk para
 * que a câmera possa esconder blocos inteiros fora da tela: uma rota tem ~14 mil
 * tiles (>20 mil sprites) e desenhar tudo a cada frame era o que travava a hunt.
 * 8x8 dá ~200 chunks num mapa de 102x113 — barato de testar, grande o bastante
 * para não fragmentar o batch de desenho.
 */
const CHUNK_TILES = 8;
/** Folga além da viewport, para o chunk já estar pronto antes de entrar em cena. */
const CULL_MARGIN = CELL;

export class MapRenderer {
  constructor(host) {
    this.host = host;
    this.app = null;
    this.layers = {};
    this.chunks = [];
    this.textures = [];
    this.ready = false;
    this.viewport = false;
    this.lastCamera = null;
    this.lastCullKey = '';
  }

  async init({
    mapDoc,
    manifest,
    drawOrder,
    pageBase = '/game/asset-packs/items/',
    viewport = false,
    canvasClass = 'city-pixi-canvas',
    // Permite separar uma camada sem duplicar a lógica de posicionamento do mapa.
    // A rota usa isso para desenhar copas/árvores acima dos atores DOM.
    renderLayers = null,
    backgroundAlpha = 1,
    // Retângulos (em tiles) cujo telhado precisa sumir quando o jogador entra no
    // prédio. Só a cidade usa; as rotas não têm interior.
    roofRects = null,
    // A cidade posiciona o mundo em DOM (#cityMapWorld com translate(-x%,-y%))
    // e o canvas precisa acompanhar exatamente esse offset. O DOM não limita a
    // câmera nas bordas, então o clamp de positionCamera dessincronizaria os
    // dois nas beiradas do mapa. A rota mantém o clamp.
    clampCamera = true,
  }) {
    this.mapDoc = mapDoc;
    this.manifest = manifest;
    this.drawOrder = drawOrder;
    this.patternMeta = buildPatternMeta(manifest);
    this.meta = mapDoc.meta;
    this.renderLayers = renderLayers ? new Set(renderLayers) : null;

    this.worldWidth = (this.meta.maxX - this.meta.minX + 1) * CELL;
    this.worldHeight = (this.meta.maxY - this.meta.minY + 1) * CELL;
    this.viewport = viewport;
    this.clampCamera = clampCamera;
    const width = viewport ? Math.max(1, Math.round(this.host.clientWidth || 1)) : this.worldWidth;
    const height = viewport ? Math.max(1, Math.round(this.host.clientHeight || 1)) : this.worldHeight;

    this.app = new Application();
    await this.app.init({
      width,
      height,
      background: 0x030605,
      backgroundAlpha,
      antialias: false,
      resolution: 1,
      autoDensity: true,
      autoStart: false,
    });

    this.host.replaceChildren(this.app.canvas);
    this.app.canvas.className = canvasClass;
    this.root = new Container();
    this.app.stage.addChild(this.root);

    // Andares presentes, do mais baixo para o mais alto. Em OTBM o eixo Z cresce
    // para baixo: 7 é o chão, 6 é o segundo piso, 5 é a cobertura. Desenhar em z
    // decrescente empilha telhado sobre piso sobre chão.
    this.floors = [...new Set(this.mapDoc.tiles.map((t) => t[2]))].sort((a, b) => b - a);
    this.roofRects = roofRects;
    this.roofGroups = new Map();
    for (const z of this.floors) {
      for (const group of this.groupsForFloor(z)) {
        for (const name of LAYER_ORDER) {
          const layer = new Container();
          layer.label = `${z}:${group}:${name}`;
          this.layers[this.layerKey(z, group, name)] = layer;
          this.root.addChild(layer);
          if (!this.roofGroups.has(group)) this.roofGroups.set(group, []);
          this.roofGroups.get(group).push(layer);
        }
      }
    }

    await this.loadTextures(pageBase);
    this.placed = this.buildMap();
    console.info(
      `[MapRenderer] ${this.placed} sprites em ${this.mapDoc.tiles.length} tiles, ${this.chunks.length} chunks`
    );
    this.ready = true;
    this.cullChunks();
    this.app.render();
    if (this.viewport) {
      // ResizeObserver e não `window.resize`: o host muda de tamanho sozinho
      // quando `--map-scale` é recalculado (ver world/map-scale.js), e um
      // listener de janela dispara antes disso — o canvas ficaria um frame
      // atrás da caixa. O observer também cobre resize/zoom, que passam pelo
      // layout do host de qualquer jeito.
      this.resizeObserver = new ResizeObserver(() => this.resizeViewport());
      this.resizeObserver.observe(this.host);
      // Backstop barato: `resizeViewport` já é no-op quando a medida não mudou.
      this.handleResize = () => this.resizeViewport();
      window.addEventListener('resize', this.handleResize);
    }
  }

  layerKey(z, group, name) {
    return `${z}|${group}|${name}`;
  }

  /**
   * O chão é uma peça só. Os andares de cima se dividem em grupos para que o
   * telhado do Centro Pokémon possa sumir sem levar junto o da loja e o das
   * outras casas — era o que os três overlays WebP faziam.
   */
  groupsForFloor(z) {
    if (z === this.meta.groundZ || !this.roofRects) return ['base'];
    return ['static', ...Object.keys(this.roofRects)];
  }

  roofGroupAt(z, x, y) {
    if (z === this.meta.groundZ || !this.roofRects) return 'base';
    for (const [name, r] of Object.entries(this.roofRects)) {
      if (x >= r.minX && x <= r.maxX && y >= r.minY && y <= r.maxY) return name;
    }
    return 'static';
  }

  /** Mostra/esconde o telhado de um grupo (`pc`, `shop`, `static`). */
  setRoofVisible(group, visible) {
    const layers = this.roofGroups?.get(group);
    if (!layers) return;
    for (const layer of layers) layer.visible = visible;
    this.app?.render();
  }

  async loadTextures(pageBase) {
    // As páginas levam o mesmo carimbo de build do manifesto: se elas puderem
    // vir do cache enquanto o manifesto é novo, cada tile lê a coordenada de
    // outro sprite (ver `versioned` em tile-utils).
    this.textures = await Promise.all(
      (this.manifest.pages || []).map((page) => Assets.load(versioned(`${pageBase}${page}`)))
    );
    // Pixel art escalado em fator fracionário (32 -> 80 px) cria costuras entre
    // tiles quando o atlas usa interpolação linear. Nearest mantém cada pixel
    // sólido e remove o "grid" sem alterar o mapa, a câmera ou a colisão.
    for (const texture of this.textures) texture.source.scaleMode = 'nearest';
  }

  createSprite(asset) {
    const frame = asset.frames?.[0];
    if (!frame) return null;
    const texture = this.textures[frame.page];
    if (!texture) return null;

    const rect = new Rectangle(frame.x, frame.y, frame.w, frame.h);
    const sprite = new Sprite(new Texture({ source: texture.source, frame: rect }));
    sprite.scale.set(SPRITE_SCALE);
    sprite.roundPixels = true;
    return sprite;
  }

  placeSprite(sprite, x, y, w, h) {
    sprite.x = (x - this.meta.minX + 1) * CELL;
    sprite.y = (y - this.meta.minY + 1) * CELL;
    sprite.anchor.set(1, 1);
    return sprite;
  }

  buildMap() {
    const groundZ = this.meta.groundZ;
    const placements = [];
    let placed = 0;

    for (const tile of this.mapDoc.tiles) {
      const [x, y, z, groundId, stack] = tile;
      if (groundId) {
        const gid = this.mapDoc?.id === 'saffron' && z === groundZ ? pcGroundId(x, y, groundId) : groundId;
        placements.push({ x, y, z, serverId: gid, layer: layerForItem(gid, this.drawOrder), isGround: true });
      }
      for (const entry of stack || []) {
        if (!entry[0]) continue;
        placements.push({
          x,
          y,
          z,
          serverId: entry[0],
          layer: layerForItem(entry[0], this.drawOrder),
          isGround: false,
        });
      }
    }

    // Andar mais baixo primeiro (z maior = mais embaixo em OTBM); dentro do
    // andar, chão antes dos objetos (o nudge do PC não pode ficar sob o tile de
    // destino).
    placements.sort((a, b) => {
      if (a.z !== b.z) return b.z - a.z;
      if (a.isGround !== b.isGround) return a.isGround ? -1 : 1;
      return (a.y - b.y) || (a.x - b.x);
    });

    // chunkStore: chave de camada -> "cx,cy" -> Container. Criado sob demanda
    // para não gerar centenas de contêineres vazios em mapas esparsos.
    const chunkStore = new Map(Object.keys(this.layers).map((key) => [key, new Map()]));

    for (const { x, y, z, serverId, layer, isGround } of placements) {
      if (this.renderLayers && !this.renderLayers.has(layer)) continue;
      if (z === groundZ && isPcSkippedItem(serverId, x, y)) continue;
      const asset = lookupAsset(this.manifest, this.patternMeta, serverId, x, y, z);
      if (!asset) continue;
      const sprite = this.createSprite(asset);
      if (!sprite) continue;
      // O nudge vale para paredes e objetos no piso jogável e no piso logo
      // abaixo do telhado. O piso do telhado permanece na posição original.
      const nudge = this.mapDoc?.id === 'saffron'
        ? pcWallNudgeForFloor(serverId, x, y, z, groundZ, { isGround })
        : { dx: 0, dy: 0 };
      const px = x + nudge.dx;
      const py = y + nudge.dy;
      this.placeSprite(sprite, px, py, asset.w, asset.h);
      const key = this.layerKey(z, this.roofGroupAt(z, px, py), layer);
      if (!chunkStore.has(key)) continue;
      this.chunkFor(chunkStore, key, px, py).addChild(sprite);
      placed++;
    }

    this.attachChunks(chunkStore);
    return placed;
  }

  /** Devolve (criando se preciso) o contêiner de chunk que cobre o tile x,y. */
  chunkFor(chunkStore, layer, x, y) {
    const cx = Math.floor((x - this.meta.minX) / CHUNK_TILES);
    const cy = Math.floor((y - this.meta.minY) / CHUNK_TILES);
    const store = chunkStore.get(layer);
    const key = `${cx},${cy}`;
    let chunk = store.get(key);
    if (!chunk) {
      chunk = new Container();
      chunk.label = `${layer}:${key}`;
      chunk.__cx = cx;
      chunk.__cy = cy;
      store.set(key, chunk);
    }
    return chunk;
  }

  /**
   * Pendura os chunks nas camadas em ordem row-major. Sprites são ancorados no
   * canto inferior direito e objetos altos invadem a linha de baixo, então a
   * ordem por linha preserva o mesmo empilhamento do desenho tile a tile.
   */
  attachChunks(chunkStore) {
    this.chunks = [];
    // A ordem das chaves é a de criação em init(): andar mais baixo primeiro,
    // e dentro do andar a LAYER_ORDER. Preserva o empilhamento.
    for (const key of Object.keys(this.layers)) {
      const store = chunkStore.get(key);
      if (!store) continue;
      const ordered = [...store.values()].sort(
        (a, b) => a.__cy - b.__cy || a.__cx - b.__cx
      );
      for (const chunk of ordered) {
        this.layers[key].addChild(chunk);
        // Objetos altos (árvores, prédios) sobem acima do próprio tile, então a
        // caixa do chunk cresce para cima para não sumir cedo demais.
        const left = chunk.__cx * CHUNK_TILES * CELL;
        const top = chunk.__cy * CHUNK_TILES * CELL;
        const size = CHUNK_TILES * CELL;
        this.chunks.push({
          container: chunk,
          left,
          top: top - CELL * 3,
          right: left + size + CELL,
          bottom: top + size + CELL,
        });
      }
    }
  }

  /**
   * Esconde os chunks fora da viewport. O root só translada (sem escala nem
   * rotação), então dá para comparar retângulos direto em coordenadas de mundo
   * e evitar o custo do Culler genérico do Pixi sobre 20 mil sprites.
   */
  cullChunks() {
    if (!this.viewport || !this.app || !this.chunks.length) return false;
    const viewLeft = -this.root.position.x - CULL_MARGIN;
    const viewTop = -this.root.position.y - CULL_MARGIN;
    const viewRight = viewLeft + this.app.screen.width + CULL_MARGIN * 2;
    const viewBottom = viewTop + this.app.screen.height + CULL_MARGIN * 2;

    // Só reavalia quando a câmera cruza um chunk; andar dentro do mesmo bloco
    // não muda o conjunto visível.
    const key = `${Math.floor(viewLeft / CELL)},${Math.floor(viewTop / CELL)},${Math.round(
      this.app.screen.width
    )},${Math.round(this.app.screen.height)}`;
    if (key === this.lastCullKey) return false;
    this.lastCullKey = key;

    let changed = false;
    for (const chunk of this.chunks) {
      const visible =
        chunk.right > viewLeft &&
        chunk.left < viewRight &&
        chunk.bottom > viewTop &&
        chunk.top < viewBottom;
      if (chunk.container.visible !== visible) {
        chunk.container.visible = visible;
        changed = true;
      }
    }
    return changed;
  }

  /**
   * Mantido para a rota, que ainda usa a camada `toppers` do próprio andar.
   * Na cidade o telhado agora é andar inteiro — use `setRoofVisible`.
   */
  setToppersVisible(visible) {
    let mudou = false;
    for (const [key, layer] of Object.entries(this.layers)) {
      if (!key.endsWith('|toppers')) continue;
      layer.visible = visible;
      mudou = true;
    }
    if (mudou) this.app?.render();
  }

  resizeViewport(render = true) {
    if (!this.viewport || !this.app) return;
    const width = Math.max(1, Math.round(this.host.clientWidth || 1));
    const height = Math.max(1, Math.round(this.host.clientHeight || 1));
    if (this.app.screen.width !== width || this.app.screen.height !== height) {
      this.app.renderer.resize(width, height);
    }
    if (this.lastCamera) this.positionCamera(this.lastCamera.x, this.lastCamera.y);
    this.cullChunks();
    if (render) this.app.render();
  }

  positionCamera(xPercent, yPercent) {
    const targetX = (Number(xPercent) / 100) * this.worldWidth;
    const targetY = (Number(yPercent) / 100) * this.worldHeight;
    const centerOrClamp = (viewportSize, worldSize, target) => {
      if (!this.clampCamera) return viewportSize / 2 - target;
      if (worldSize <= viewportSize) return (viewportSize - worldSize) / 2;
      return Math.max(viewportSize - worldSize, Math.min(0, viewportSize / 2 - target));
    };
    const x = centerOrClamp(this.app.screen.width, this.worldWidth, targetX);
    const y = centerOrClamp(this.app.screen.height, this.worldHeight, targetY);
    this.root.position.set(x, y);
    return { x, y };
  }

  setCameraPercent(xPercent, yPercent) {
    if (!this.viewport || !this.app) return;
    const x = Number(xPercent) || 50;
    const y = Number(yPercent) || 50;
    const prev = this.lastCamera;
    // Skip work when the chase target is standing still (combat). Re-reading
    // host size + full-scene render every RAF was freezing large hunts.
    const same =
      prev &&
      Math.abs(prev.x - x) < 0.0005 &&
      Math.abs(prev.y - y) < 0.0005;
    this.lastCamera = { x, y };
    const offset = this.positionCamera(x, y);
    const reculled = this.cullChunks();
    if (!same || reculled) this.app.render();
    return offset;
  }

  destroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    this.app?.destroy(true, { children: true });
    this.app = null;
    this.ready = false;
    this.lastCamera = null;
    this.lastCullKey = '';
    this.renderLayers = null;
    this.chunks = [];
  }
}
